import React, {
  useEffect,
  useRef
} from "react";

import {
  Box,
  IconButton,
  Tooltip
} from "@material-ui/core";

import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatUnderlinedIcon from "@material-ui/icons/FormatUnderlined";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import FormatListNumberedIcon from "@material-ui/icons/FormatListNumbered";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import LinkIcon from "@material-ui/icons/Link";
import UndoIcon from "@material-ui/icons/Undo";
import RedoIcon from "@material-ui/icons/Redo";


const escapeHtml = value =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");


const InlineImageIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="3"
      y="4"
      width="18"
      height="16"
      rx="2"
      stroke="currentColor"
      strokeWidth="2"
    />

    <circle
      cx="8.5"
      cy="9"
      r="1.5"
      fill="currentColor"
    />

    <path
      d="M5 18l4.5-4.5 3 3 2.5-2.5L19 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);


const RichEmailEditor = ({
  valueHtml = "",
  valueText = "",
  onChange,

  // KORAX INLINE BODY IMAGES V1
  inlineImages = [],
  onInlineImagesChange,

  disabled = false,
  minHeight = 180
}) => {

  const editorRef =
    useRef(null);

  const rangeRef =
    useRef(null);

  const emittedRef =
    useRef("");

  const imageInputRef =
    useRef(null);


  const rememberSelection = () => {

    const selection =
      window.getSelection();

    if (
      !selection ||
      !selection.rangeCount ||
      !editorRef.current
    ) {
      return;
    }


    const range =
      selection.getRangeAt(0);


    if (
      editorRef.current.contains(
        range.commonAncestorContainer
      )
    ) {

      rangeRef.current =
        range.cloneRange();
    }
  };


  const restoreSelection = () => {

    if (!rangeRef.current) {
      return;
    }


    const selection =
      window.getSelection();

    if (!selection) {
      return;
    }


    selection.removeAllRanges();

    selection.addRange(
      rangeRef.current
    );
  };


  const buildOutgoingHtml = () => {

    const editor =
      editorRef.current;

    if (!editor) {
      return "";
    }


    const clone =
      editor.cloneNode(true);


    Array.from(
      clone.querySelectorAll(
        "img[data-korax-cid]"
      )
    ).forEach(
      image => {

        const cid =
          String(
            image.getAttribute(
              "data-korax-cid"
            ) ||
            ""
          ).trim();


        if (cid) {

          image.setAttribute(
            "src",
            `cid:${cid}`
          );
        }


        image.removeAttribute(
          "data-korax-cid"
        );
      }
    );


    return String(
      clone.innerHTML ||
      ""
    ).trim();
  };


  const emit = () => {

    const editor =
      editorRef.current;

    if (!editor) {
      return;
    }


    const html =
      buildOutgoingHtml();


    const text =
      String(
        editor.innerText ||
        ""
      )
        .replace(
          /\u00a0/g,
          " "
        )
        .trim();


    emittedRef.current =
      html;


    if (
      typeof onChange ===
      "function"
    ) {

      onChange({
        html,
        text
      });
    }
  };


  const exec = (
    command,
    value = null
  ) => {

    if (disabled) {
      return;
    }


    restoreSelection();


    document.execCommand(
      command,
      false,
      value
    );


    editorRef.current
      ?.focus();


    rememberSelection();

    emit();
  };


  const insertLink = () => {

    const input =
      window.prompt(
        "Cole o endereço do link:"
      );


    if (!input) {
      return;
    }


    let url =
      String(
        input
      ).trim();


    if (
      !/^https?:\/\//i.test(
        url
      ) &&
      !/^mailto:/i.test(
        url
      ) &&
      !/^tel:/i.test(
        url
      )
    ) {

      url =
        `https://${url}`;
    }


    exec(
      "createLink",
      url
    );
  };


  const openImagePicker = () => {

    if (disabled) {
      return;
    }


    rememberSelection();


    imageInputRef.current
      ?.click();
  };


  const handleImageFile =
    event => {

      const file =
        event.target.files?.[0];


      event.target.value =
        "";


      if (!file) {
        return;
      }


      const allowed = [
        "image/png",
        "image/jpeg",
        "image/webp"
      ];


      if (
        !allowed.includes(
          file.type
        )
      ) {

        window.alert(
          "Use uma imagem PNG, JPG ou WebP."
        );

        return;
      }


      if (
        file.size >
        3 * 1024 * 1024
      ) {

        window.alert(
          "A imagem deve ter no máximo 3 MB."
        );

        return;
      }


      const reader =
        new FileReader();


      reader.onload =
        () => {

          const dataUrl =
            String(
              reader.result ||
              ""
            );


          const comma =
            dataUrl.indexOf(
              ","
            );


          if (
            comma === -1 ||
            !dataUrl.startsWith(
              "data:image/"
            )
          ) {

            window.alert(
              "Não foi possível carregar esta imagem."
            );

            return;
          }


          const content =
            dataUrl.slice(
              comma + 1
            );


          const cid =
            `korax-inline-${Date.now()}-` +
            `${Math.random()
              .toString(36)
              .slice(2, 10)}`;


          const newImage = {
            filename:
              file.name ||
              "imagem",

            contentType:
              file.type,

            content,

            cid,

            dataUrl
          };


          if (
            typeof onInlineImagesChange ===
            "function"
          ) {

            onInlineImagesChange([
              ...inlineImages,
              newImage
            ]);
          }


          const editor =
            editorRef.current;


          if (!editor) {
            return;
          }


          editor.focus();

          restoreSelection();


          const img =
            document.createElement(
              "img"
            );


          img.setAttribute(
            "src",
            dataUrl
          );

          img.setAttribute(
            "alt",
            file.name ||
            "Imagem"
          );

          img.setAttribute(
            "data-korax-cid",
            cid
          );


          img.style.display =
            "block";

          img.style.maxWidth =
            "100%";

          img.style.width =
            "auto";

          img.style.height =
            "auto";

          img.style.maxHeight =
            "720px";

          img.style.margin =
            "12px 0";

          img.style.border =
            "0";


          const br =
            document.createElement(
              "br"
            );


          const selection =
            window.getSelection();


          let range =
            rangeRef.current;


          if (
            range &&
            editor.contains(
              range.commonAncestorContainer
            )
          ) {

            range =
              range.cloneRange();

            range.deleteContents();

            range.insertNode(
              img
            );

            range.setStartAfter(
              img
            );

            range.collapse(
              true
            );

            range.insertNode(
              br
            );

            range.setStartAfter(
              br
            );

            range.collapse(
              true
            );


            if (selection) {

              selection.removeAllRanges();

              selection.addRange(
                range
              );
            }


            rangeRef.current =
              range.cloneRange();

          } else {

            editor.appendChild(
              img
            );

            editor.appendChild(
              br
            );
          }


          emit();

          editor.focus();
        };


      reader.onerror =
        () => {

          window.alert(
            "Não foi possível carregar esta imagem."
          );
        };


      reader.readAsDataURL(
        file
      );
    };


  useEffect(
    () => {

      const editor =
        editorRef.current;

      if (!editor) {
        return;
      }


      const html =
        String(
          valueHtml ||
          ""
        );


      if (
        html ===
        emittedRef.current
      ) {
        return;
      }


      if (html) {

        editor.innerHTML =
          html;


        /*
         * Caso o estado venha a ser restaurado
         * dentro da mesma composição, mostramos
         * novamente a imagem local no editor.
         */
        Array.from(
          editor.querySelectorAll(
            'img[src^="cid:"]'
          )
        ).forEach(
          image => {

            const src =
              String(
                image.getAttribute(
                  "src"
                ) ||
                ""
              );


            const cid =
              src.replace(
                /^cid:/i,
                ""
              );


            const found =
              inlineImages.find(
                item =>
                  String(
                    item?.cid ||
                    ""
                  ) ===
                  cid
              );


            if (
              found?.dataUrl
            ) {

              image.setAttribute(
                "src",
                found.dataUrl
              );

              image.setAttribute(
                "data-korax-cid",
                cid
              );
            }
          }
        );


        emittedRef.current =
          html;

        return;
      }


      editor.innerHTML =
        valueText
          ? escapeHtml(
              valueText
            ).replace(
              /\n/g,
              "<br />"
            )
          : "";


      emittedRef.current =
        "";

    },
    [
      valueHtml,
      valueText,
      inlineImages
    ]
  );


  const button = (
    title,
    icon,
    action
  ) => (

    <Tooltip
      title={title}
    >
      <span>

        <IconButton
          size="small"
          disabled={disabled}
          onMouseDown={
            event => {

              event.preventDefault();

              rememberSelection();
            }
          }
          onClick={action}
          style={{
            width: 32,
            height: 32,
            borderRadius: 7
          }}
        >
          {icon}
        </IconButton>

      </span>
    </Tooltip>
  );


  const separator = () => (

    <Box
      style={{
        width: 1,
        height: 22,
        margin:
          "0 4px",
        background:
          "#D0D5DD"
      }}
    />
  );


  return (

    <Box>

      <input
        ref={
          imageInputRef
        }
        type="file"
        hidden
        accept="image/png,image/jpeg,image/webp"
        onChange={
          handleImageFile
        }
      />


      <Box
        display="flex"
        alignItems="center"
        style={{
          gap: 2,
          padding: 6,
          flexWrap:
            "wrap",
          border:
            "1px solid #D0D5DD",
          borderBottom: 0,
          borderRadius:
            "10px 10px 0 0",
          background:
            "#F9FAFB"
        }}
      >

        {button(
          "Negrito",
          <FormatBoldIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "bold"
            )
        )}


        {button(
          "Itálico",
          <FormatItalicIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "italic"
            )
        )}


        {button(
          "Sublinhado",
          <FormatUnderlinedIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "underline"
            )
        )}


        {separator()}


        {button(
          "Lista com marcadores",
          <FormatListBulletedIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "insertUnorderedList"
            )
        )}


        {button(
          "Lista numerada",
          <FormatListNumberedIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "insertOrderedList"
            )
        )}


        {separator()}


        {button(
          "Alinhar à esquerda",
          <FormatAlignLeftIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "justifyLeft"
            )
        )}


        {button(
          "Centralizar",
          <FormatAlignCenterIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "justifyCenter"
            )
        )}


        {button(
          "Alinhar à direita",
          <FormatAlignRightIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "justifyRight"
            )
        )}


        {separator()}


        {button(
          "Inserir link",
          <LinkIcon
            style={{
              fontSize: 18
            }}
          />,
          insertLink
        )}


        {button(
          "Inserir imagem",
          <InlineImageIcon />,
          openImagePicker
        )}


        {separator()}


        {button(
          "Desfazer",
          <UndoIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "undo"
            )
        )}


        {button(
          "Refazer",
          <RedoIcon
            style={{
              fontSize: 18
            }}
          />,
          () =>
            exec(
              "redo"
            )
        )}

      </Box>


      <Box
        ref={
          editorRef
        }
        contentEditable={
          !disabled
        }
        suppressContentEditableWarning
        onInput={
          () => {

            rememberSelection();

            emit();
          }
        }
        onMouseUp={
          rememberSelection
        }
        onKeyUp={
          rememberSelection
        }
        onFocus={
          rememberSelection
        }
        style={{
          minHeight,
          maxHeight: 480,
          overflowY:
            "auto",
          padding:
            "12px 14px",
          outline:
            "none",
          border:
            "1px solid #D0D5DD",
          borderRadius:
            "0 0 10px 10px",
          background:
            disabled
              ? "#F9FAFB"
              : "#FFFFFF",
          color:
            "#344054",
          fontFamily:
            "Arial, Helvetica, sans-serif",
          fontSize: 13,
          lineHeight: 1.65,
          overflowWrap:
            "anywhere"
        }}
      />

    </Box>
  );
};


export default
  RichEmailEditor;
